/**
 * ApiKeyField — R3-S3 (F3.4)
 *
 * Wiederverwendbares Eingabefeld fuer ein Geheimnis (API-Key), das
 * standardmaessig maskiert ist und per Toggle aufgedeckt bzw. per Button
 * kopiert werden kann.
 *
 * Herausgeloest aus features/settings/ApiKeySection.tsx, damit der
 * Developer-Bereich (API-Doku-Seite) dieselbe Darstellung ohne Duplikat nutzt.
 * Die Labels kommen vom Aufrufer, weil die beiden Nutzungsorte
 * unterschiedliche i18n-Namespaces verwenden (settings vs. developer).
 */
import { useState } from 'react'

export type ApiKeyFieldLabels = {
  ariaLabel: string
  show: string
  hide: string
  showAria: string
  hideAria: string
  copy: string
  copyAria: string
}

type Props = {
  /** Roher Key — wird nur beim Aufdecken/Kopieren verwendet. */
  apiKey: string
  /** Vorberechnete Maske (z. B. api_key_masked vom Backend). */
  maskedValue?: string | null
  labels: ApiKeyFieldLabels
  /** Prefix fuer die data-testid-Attribute (Default: "api-key"). */
  testIdPrefix?: string
}

function maskApiKey(apiKey: string): string {
  return `****${apiKey.slice(-4)}`
}

export function ApiKeyField({ apiKey, maskedValue, labels, testIdPrefix = 'api-key' }: Props) {
  const [visible, setVisible] = useState(false)

  const masked = maskedValue && maskedValue.length > 0 ? maskedValue : maskApiKey(apiKey)

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(apiKey)
    } catch {
      // Clipboard-API nicht verfügbar
    }
  }

  return (
    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
      <input
        type="text"
        readOnly
        className="input"
        value={visible ? apiKey : masked}
        aria-label={labels.ariaLabel}
        data-testid={`${testIdPrefix}-input`}
        style={{ flex: 1, fontFamily: 'monospace', fontSize: 13 }}
      />
      <button
        type="button"
        className="btn btn-sm btn-ghost"
        onClick={() => setVisible((v) => !v)}
        data-testid={`${testIdPrefix}-toggle`}
        aria-label={visible ? labels.hideAria : labels.showAria}
      >
        {visible ? labels.hide : labels.show}
      </button>
      <button
        type="button"
        className="btn btn-sm btn-ghost"
        onClick={() => { void handleCopy() }}
        data-testid={`${testIdPrefix}-copy`}
        aria-label={labels.copyAria}
      >
        {labels.copy}
      </button>
    </div>
  )
}
